/**
 * EmailSetup.tsx — customer-facing email setup wizard.
 *
 * Route: /dashboard/email/setup/:domainId
 *
 * What the customer sees here:
 *   - Big status indicator (Awaiting DNS / Verifying / Ready)
 *   - Table of DNS records to paste at their registrar, each with a
 *     copy-to-clipboard button and a live ✓/⚠ indicator
 *   - Re-check button + auto-poll every 5s (mirrors the host density
 *     dashboard pattern that the user explicitly asked for)
 *   - Provisioning timeline so the customer sees the system *doing*
 *     things, not just sitting there
 *
 * Colour policy: this is a customer surface, so we stay in the logo
 * palette (navy + yellow + white + neutral grays). The exception we
 * granted to the host density admin page does NOT apply here. Record
 * visibility uses ✓ icon + brand yellow, not green; "not yet" uses ⚠
 * icon + a neutral background, not red. Failed state uses navy + 🚨.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { getApiUrl } from '../config/environment';
import { MailConnectionFields, resolveMailSettings } from '../config/mail';
import DashShell from '../components/dash/DashShell';

interface DnsRecord {
  label: string;
  host: string;
  type: 'MX' | 'TXT' | 'CNAME' | 'A';
  value: string;
  visible: boolean;
  required: boolean;
  help: string;
  /** v2 only: 'ownership' marks the _12and22-verify TXT record. */
  kind?: string;
}

interface SetupData extends MailConnectionFields {
  domain: {
    id: string;
    domain: string;
    status: string;
    domain_action: 'register' | 'transfer' | 'existing';
    status_detail: string | null;
    ses_identity_status: string;
    webmail_installed?: boolean;
    last_error: string | null;
    last_error_public?: string | null;
    created_at: string;
    updated_at: string;
  };
  records: DnsRecord[];
  events: Array<{
    event_type: string;
    from_status: string | null;
    to_status: string | null;
    severity: 'info' | 'warning' | 'error';
    message: string;
    created_at: string;
  }>;
  ui_stage: 'awaiting_dns' | 'provisioning' | 'complete' | 'failed' | 'zone_unreachable' | 'suspended' | 'terminated';
  status_detail?: string | null;
  last_error_public?: string | null;
}

const POLL_MS = 5_000;

const fmtAgo = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};

// One row in the DNS records table. Copy-to-clipboard is the single
// thing customers do most on this page, so make it visible + obvious.
const RecordRow: React.FC<{ rec: DnsRecord }> = ({ rec }) => {
  const [copied, setCopied] = useState<'host' | 'value' | null>(null);
  const copy = async (which: 'host' | 'value', text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(null), 1500);
    } catch { /* clipboard might be unavailable; user can select+copy */ }
  };

  // Visibility chip: brand yellow when seen, neutral when not. We deliberately
  // do not use red — this is a customer surface and red would look like an
  // error rather than "waiting for your DNS to propagate."
  const chip = rec.visible
    ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary-500 text-primary-navy font-semibold">✓ Visible</span>
    : <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-300">⏳ Waiting</span>;

  const isOwnership = rec.kind === 'ownership';
  return (
    <div className={`border rounded-lg p-4 ${rec.visible ? 'bg-secondary-50 border-secondary-200' : 'bg-white border-gray-200'}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="font-medium text-primary-navy">{isOwnership ? 'Domain ownership' : rec.label}</div>
          <div className="text-xs text-gray-500">
            {rec.help}
            {isOwnership && ' Once this is seen, a second batch of records (MX, SPF, DKIM, DMARC) appears below.'}
          </div>
        </div>
        {chip}
      </div>
      <span className="sr-only" aria-live="polite">
        {copied ? `${copied === 'host' ? 'Host' : 'Value'} copied to clipboard` : ''}
      </span>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 text-sm">
        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-wider text-gray-500">Type</div>
          <div className="font-mono text-primary-navy">{rec.type}</div>
        </div>
        <div className="md:col-span-3">
          <div className="text-xs uppercase tracking-wider text-gray-500">Host / Name</div>
          <button
            type="button"
            onClick={() => copy('host', rec.host)}
            className="font-mono text-primary-navy hover:bg-secondary-100 px-1 -mx-1 rounded text-left w-full break-all"
            title="Copy host"
            aria-label={`Copy host ${rec.host}`}
          >
            {rec.host} {copied === 'host' && <span className="text-xs text-secondary-700 ml-2">copied</span>}
          </button>
        </div>
        <div className="md:col-span-7">
          <div className="text-xs uppercase tracking-wider text-gray-500">Value</div>
          <button
            type="button"
            onClick={() => copy('value', rec.value)}
            className="font-mono text-primary-navy hover:bg-secondary-100 px-1 -mx-1 rounded text-left w-full break-all"
            title="Copy value"
            aria-label={`Copy value for ${rec.label}`}
          >
            {rec.value} {copied === 'value' && <span className="text-xs text-secondary-700 ml-2">copied</span>}
          </button>
        </div>
      </div>
    </div>
  );
};

// Top-of-page status indicator. Three visual states matching ui_stage.
const StageBanner: React.FC<{ data: SetupData }> = ({ data }) => {
  const { ui_stage, domain } = data;
  if (ui_stage === 'complete') {
    return (
      <div className="rounded-2xl bg-gradient-to-r from-primary-navy via-primary-700 to-secondary-500 p-6 text-white shadow-lg">
        <div className="flex items-center gap-4">
          <span className="text-3xl">✓</span>
          <div>
            <h2 className="text-2xl font-bold">{domain.domain} is set up</h2>
            <p className="text-sm text-white/90 mt-1">
              Your DNS is verified, mailbox infrastructure is live, you're ready to receive mail.
            </p>
          </div>
        </div>
      </div>
    );
  }
  const publicError = data.last_error_public || domain.last_error_public || domain.last_error;
  const detail = data.status_detail || domain.status_detail;
  if (ui_stage === 'failed') {
    return (
      <div className="rounded-2xl bg-secondary-200 border-2 border-secondary-500 p-6" role="alert">
        <div className="flex items-center gap-4">
          <span className="text-3xl" aria-hidden>🚨</span>
          <div>
            <h2 className="text-2xl font-bold text-primary-navy">Setup needs attention</h2>
            <p className="text-sm text-primary-navy/80 mt-1">
              Something went wrong. {publicError || 'Contact support and reference this domain.'}
            </p>
            <Link to="/contact" className="inline-block mt-2 text-sm bg-primary-navy text-white px-3 py-1.5 rounded-lg hover:bg-primary-700">
              Contact support
            </Link>
          </div>
        </div>
      </div>
    );
  }
  if (ui_stage === 'suspended') {
    return (
      <div className="rounded-2xl bg-secondary-200 border-2 border-primary-navy p-6" role="alert">
        <div className="flex items-center gap-4">
          <span className="text-3xl" aria-hidden>⚠</span>
          <div>
            <h2 className="text-2xl font-bold text-primary-navy">Mail for {domain.domain} is suspended</h2>
            <p className="text-sm text-primary-navy/90 mt-1">{detail || publicError || 'Check your billing status, or contact support.'}</p>
            <div className="mt-2 flex gap-2 flex-wrap">
              <Link to="/dashboard/billing" className="text-sm bg-primary-navy text-white px-3 py-1.5 rounded-lg hover:bg-primary-700">Billing</Link>
              <Link to="/contact" className="text-sm border border-primary-navy text-primary-navy px-3 py-1.5 rounded-lg hover:bg-secondary-50">Contact support</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (ui_stage === 'terminated') {
    return (
      <div className="rounded-2xl bg-gray-100 border-2 border-gray-300 p-6">
        <div className="flex items-center gap-4">
          <span className="text-3xl" aria-hidden>·</span>
          <div>
            <h2 className="text-2xl font-bold text-primary-navy">This email service has been cancelled</h2>
            <p className="text-sm text-primary-navy/80 mt-1">
              {detail || `Mail for ${domain.domain} is no longer hosted here. You can start a new subscription any time.`}
            </p>
            <Link to="/email" className="inline-block mt-2 text-sm underline text-primary-navy">Browse email plans</Link>
          </div>
        </div>
      </div>
    );
  }
  if (ui_stage === 'zone_unreachable') {
    // Distinct from `failed`: the customer's *registrar* did this, not
    // us. We surface the registrar-specific message verbatim because
    // the recovery path is on their side, not ours.
    return (
      <div className="rounded-2xl bg-secondary-200 border-2 border-primary-navy p-6">
        <div className="flex items-center gap-4">
          <span className="text-3xl">🚨</span>
          <div>
            <h2 className="text-2xl font-bold text-primary-navy">Your domain is offline at the registrar</h2>
            <p className="text-sm text-primary-navy/90 mt-1">
              {domain.last_error || 'The registrar has suspended this domain. Log into your registrar account to fix the cause.'}
            </p>
            <p className="text-xs text-primary-navy/70 mt-2">
              Mail to and from <strong>{domain.domain}</strong> will not work until this is resolved.
              This page re-checks automatically — once you fix it at your registrar, the banner will clear within an hour.
            </p>
          </div>
        </div>
      </div>
    );
  }
  if (ui_stage === 'awaiting_dns') {
    return (
      <div className="rounded-2xl bg-secondary-50 border-2 border-secondary-300 p-6">
        <div className="flex items-center gap-4">
          <span className="text-3xl">⏳</span>
          <div>
            <h2 className="text-2xl font-bold text-primary-navy">Waiting for DNS records</h2>
            <p className="text-sm text-primary-navy/80 mt-1">
              Paste the records below at your domain registrar. We check every 60 seconds and
              this page refreshes live as records propagate.
            </p>
            {detail && <p className="text-xs text-primary-navy/70 mt-1">{detail}</p>}
          </div>
        </div>
      </div>
    );
  }
  // provisioning — DNS is in, system is finishing setup.
  return (
    <div className="rounded-2xl bg-gradient-to-r from-primary-navy to-primary-700 p-6 text-white shadow-lg">
      <div className="flex items-center gap-4">
        <span className="h-10 w-10 rounded-full border-4 border-secondary-500 border-t-transparent animate-spin" />
        <div>
          <h2 className="text-2xl font-bold">Finishing your setup…</h2>
          <p className="text-sm text-white/90 mt-1">
            {detail || "DNS is verified. We're creating your mailbox infrastructure now. Usually under a minute."}
          </p>
        </div>
      </div>
    </div>
  );
};

// Timeline view of provisioning events. Keeps the customer informed
// and demonstrates the system is doing real work. Truncates to the
// last 8 to avoid wall-of-text.
const TimelinePanel: React.FC<{ events: SetupData['events'] }> = ({ events }) => {
  if (!events.length) return null;
  const recent = events.slice(0, 8);
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-3">Recent activity</div>
      <ol className="space-y-3">
        {recent.map((e, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className={`mt-1.5 w-2 h-2 rounded-full flex-none ${
              e.severity === 'error'   ? 'bg-primary-navy' :
              e.severity === 'warning' ? 'bg-secondary-500' :
                                         'bg-secondary-300'
            }`} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-primary-navy break-words">{e.message}</div>
              <div className="text-xs text-gray-500">
                {e.from_status && e.to_status ? `${e.from_status} → ${e.to_status} · ` : ''}
                {fmtAgo(e.created_at)}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

const EmailSetup: React.FC = () => {
  // The page is mounted on two routes:
  //   /dashboard/email/setup/:domainId        (v1: legacy customer_mail_domains)
  //   /dashboard/email/setup/v2/:id           (v2: mx1_mail_domains — Business Email v2)
  // useParams gives us :domainId for v1 and :id for v2; we accept either.
  // useLocation tells us which path matched so we can hit the right endpoint.
  const params = useParams<{ domainId?: string; id?: string }>();
  const domainId = params.domainId || params.id;
  const location = useLocation();
  const isV2 = location.pathname.includes('/setup/v2/');
  const setupEndpoint   = isV2 ? `/email/setup/v2/${domainId}`         : `/email/setup/${domainId}`;
  const recheckEndpoint = isV2 ? `/email/setup/v2/${domainId}/recheck` : `/email/setup/${domainId}/recheck`;

  const [data, setData] = useState<SetupData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [pulse, setPulse] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [, setTick] = useState(0);
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (!domainId) return;
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const r = await fetch(getApiUrl(setupEndpoint), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const j = await r.json();
      if (!j.success) { setError(j.error || 'Failed to load setup'); return; }
      setData(j.data as SetupData);
      setError(null);
      setLastUpdate(new Date());
      setPulse(true);
      setTimeout(() => setPulse(false), 400);
    } catch (e: any) {
      setError(e?.message || 'Network error');
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }, [domainId, setupEndpoint]);

  const recheck = useCallback(async () => {
    if (!domainId) return;
    setRechecking(true);
    try {
      await fetch(getApiUrl(recheckEndpoint), {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      // The recheck endpoint runs the poller, which writes new events
      // and may transition state. Pull fresh data.
      await load();
    } catch (e: any) {
      setError(e?.message || 'Re-check failed');
    } finally {
      setRechecking(false);
    }
  }, [domainId, recheckEndpoint, load]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      load();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  if (loading && !data) {
    return (
      <DashShell title="Email setup" subtitle="Loading provisioning status…">
        <div className="h-32 rounded-2xl bg-white/60 backdrop-blur animate-pulse mb-6" />
        <div className="h-64 rounded-2xl bg-white/60 backdrop-blur animate-pulse" />
      </DashShell>
    );
  }
  if (error || !data) {
    return (
      <DashShell title="Email setup" subtitle="Setup not loaded">
        <div className="bg-secondary-100 border border-secondary-500 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-primary-navy">Couldn't load setup</h2>
          <p className="text-sm text-primary-navy/80 mt-1">{error || 'Unknown error'}</p>
          <Link to="/customer/email" className="text-sm text-primary-navy underline mt-3 inline-block">
            ← Back to email dashboard
          </Link>
        </div>
      </DashShell>
    );
  }

  const secondsAgo = lastUpdate ? Math.max(0, Math.floor((Date.now() - lastUpdate.getTime()) / 1000)) : null;
  const records = Array.isArray(data.records) ? data.records : [];
  const requiredRecords = records.filter(r => r.required);
  const visibleRequired = requiredRecords.filter(r => r.visible).length;
  const hasOwnership = records.some(r => r.kind === 'ownership');

  // Connection settings: v2 prefers the API's hostnames (MAIL_HOSTS is the
  // fallback). v1 (legacy hosting-bundle mail) keeps its per-domain webmail
  // vhost when installed, otherwise the shared hosting webmail.
  const mail = resolveMailSettings(data);
  const webmailUrl = isV2
    ? mail.webmailUrl
    : (data.domain.webmail_installed ? `https://webmail.${data.domain.domain}` : 'https://webmail.12and22.com');

  const livePill = (
    <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-white/40 rounded-full px-3 py-1.5 shadow-sm">
      <span className={`h-2 w-2 rounded-full bg-secondary-500 ${pulse ? 'animate-ping' : ''}`} />
      <span className="text-xs font-medium text-primary-navy">Live</span>
      {secondsAgo !== null && (
        <span className="text-xs text-gray-500 tabular-nums">
          · updated {secondsAgo < 1 ? 'now' : `${secondsAgo}s ago`}
        </span>
      )}
    </div>
  );

  return (
    <DashShell
      title="Email setup"
      subtitle={`Provisioning for ${data.domain.domain}`}
      right={livePill}
    >
      <div className="space-y-6">
        <div>
          <Link to="/customer/email" className="text-sm text-primary-navy hover:underline">
            ← Email dashboard
          </Link>
        </div>

        <StageBanner data={data} />

      {/* DNS records section — the heart of the wizard */}
      {(data.ui_stage === 'awaiting_dns' || data.ui_stage === 'provisioning') && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-primary-navy">DNS records</h3>
              <p className="text-sm text-gray-600 mt-1">
                Add these at your domain registrar (GoDaddy, Namecheap, Cloudflare, etc.).
                Click any value to copy. {visibleRequired} of {requiredRecords.length} required records visible.
              </p>
            </div>
            <button
              type="button"
              onClick={recheck}
              disabled={rechecking}
              className="text-sm bg-secondary-500 text-primary-navy font-medium px-3 py-1.5 rounded-lg hover:bg-secondary-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {rechecking ? 'Checking…' : 'Re-check now'}
            </button>
          </div>
          <div className="space-y-3">
            {records.map((rec, i) => <RecordRow key={i} rec={rec} />)}
          </div>
          {hasOwnership && (
            <p className="text-xs text-gray-500 mt-3">
              Where your registrar shows a host of <span className="font-mono">@</span>, that means the domain itself (the apex) — enter it exactly as shown, or leave the host empty if your registrar uses a blank field for the apex.
            </p>
          )}
        </div>
      )}

      {/* Once active — show working settings the customer can use */}
      {data.ui_stage === 'complete' && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-lg font-semibold text-primary-navy mb-3">You're live</h3>
          <p className="text-sm text-gray-700 mb-4">
            Mail for <strong>{data.domain.domain}</strong> is now flowing through 12and22.
            Configure your client with the settings below, or head to your mailboxes panel
            to set up users.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-3">
              <div className="text-xs uppercase tracking-wider text-gray-500">Webmail</div>
              <a href={webmailUrl} target="_blank" rel="noopener noreferrer" className="font-mono text-primary-navy underline break-all">
                {webmailUrl}
              </a>
            </div>
            <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-3">
              <div className="text-xs uppercase tracking-wider text-gray-500">IMAP</div>
              <div className="font-mono text-primary-navy">{mail.imapHost}:{mail.imapPort} (TLS)</div>
            </div>
            <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-3">
              <div className="text-xs uppercase tracking-wider text-gray-500">SMTP</div>
              <div className="font-mono text-primary-navy">{mail.smtpHost}:{mail.smtpPort} (STARTTLS)</div>
            </div>
            <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-3">
              <div className="text-xs uppercase tracking-wider text-gray-500">POP3</div>
              <div className="font-mono text-primary-navy">{mail.pop3Host}:{mail.pop3Port} (TLS)</div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to={isV2 ? `/dashboard/email/mailboxes/v2/${data.domain.id}` : `/dashboard/email/mailboxes/${data.domain.id}`}
              className="text-sm bg-primary-navy text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition"
            >
              Manage mailboxes →
            </Link>
            <Link
              to={isV2 ? `/dashboard/email/migrate/v2/${data.domain.id}` : `/dashboard/email/migrate/${data.domain.id}`}
              className="text-sm bg-secondary-500 text-primary-navy font-medium px-4 py-2 rounded-lg hover:bg-secondary-600 transition"
            >
              Migrate from another provider →
            </Link>
          </div>
        </div>
      )}

        <TimelinePanel events={Array.isArray(data.events) ? data.events : []} />
      </div>
    </DashShell>
  );
};

export default EmailSetup;
